<script setup lang="ts">
import {
  RadioGroup,
  RadioGroupOption,
} from "@headlessui/vue";
import NumberFlow from "@number-flow/vue";
import { NuxtLinkLocale } from "#components";
import { stripeConfig } from "~/utils/stripe-config";

const { t, tm, rt } = useI18n();

interface Props {
  showHeader?: boolean;
  showFrequencyToggle?: boolean;
  ctaText?: string;
  ctaAction?: (tier: any) => void;
  compact?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showHeader: true,
  showFrequencyToggle: true,
  ctaText: undefined,
  compact: false,
});

const { pricing: pricingData } = usePricing();

const frequencies = [
  { value: "monthly" as const, label: t("pricing.monthly"), priceSuffix: t("pricing.perMonth") },
  { value: "annually" as const, label: t("pricing.annually"), priceSuffix: t("pricing.perMonth") },
];

const tiers = computed(() => [
  {
    name: t("pricing.tiers.starter.name"),
    id: "tier-starter",
    href: "#",
    price: pricingData.value?.Starter || { monthly: 0, annually: 0, annuallyTotal: 0 },
    priceFormatted: {
      monthly: `${pricingData.value?.Starter?.monthly?.toFixed(2) || "0.00"}€`,
      annually: `${pricingData.value?.Starter?.annually?.toFixed(2) || "0.00"}€`,
    },
    description: t("pricing.tiers.starter.description"),
    features: tm("pricing.tiers.starter.features").map(rt),
    mostPopular: false,
    stripeProductId: stripeConfig.products.Starter.productId,
    stripePriceIds: stripeConfig.products.Starter.priceIds,
  },
  {
    name: t("pricing.tiers.pro.name"),
    id: "tier-pro",
    href: "#",
    price: pricingData.value?.Pro || { monthly: 0, annually: 0, annuallyTotal: 0 },
    priceFormatted: {
      monthly: `${pricingData.value?.Pro?.monthly?.toFixed(2) || "0.00"}€`,
      annually: `${pricingData.value?.Pro?.annually?.toFixed(2) || "0.00"}€`,
    },
    description: t("pricing.tiers.pro.description"),
    features: tm("pricing.tiers.pro.features").map(rt),
    mostPopular: true,
    stripeProductId: stripeConfig.products.Pro.productId,
    stripePriceIds: stripeConfig.products.Pro.priceIds,
  },
  {
    name: t("pricing.tiers.team.name"),
    id: "tier-team",
    href: "#",
    price: pricingData.value?.Team || { monthly: 0, annually: 0, annuallyTotal: 0 },
    priceFormatted: {
      monthly: `${pricingData.value?.Team?.monthly?.toFixed(2) || "0.00"}€`,
      annually: `${pricingData.value?.Team?.annually?.toFixed(2) || "0.00"}€`,
    },
    description: t("pricing.tiers.team.description"),
    features: tm("pricing.tiers.team.features").map(rt),
    mostPopular: false,
    stripeProductId: stripeConfig.products.Team.productId,
    stripePriceIds: stripeConfig.products.Team.priceIds,
  },
]);

const frequency = ref(frequencies[1]);

function handleCtaClick(tier: ReturnType<typeof tiers.value[number]>) {
  safeCapture("pricing_cta_click", {
    tier: tier.name,
    frequency: frequency.value.value,
    source: "landing",
    hasCustomCta: Boolean(props.ctaAction),
  });

  if (props.ctaAction) {
    props.ctaAction({
      ...tier,
      selectedFrequency: frequency.value,
    });
  } else {
    const selectedPlan = useCookie("selectedPlan");
    selectedPlan.value = tier.name;
  }
}
</script>

<template>
  <div
    id="tarifs"
    :class="[compact ? '' : 'mx-auto my-24 max-w-7xl scroll-mt-10 px-6 sm:my-32 lg:px-8 xl:my-48']"
  >
    <div
      v-if="showHeader"
      class="mx-auto max-w-4xl text-center"
    >
      <h2
        class="scroll-mt-20 text-base/7 font-semibold text-primary-600"
      >
        {{ $t('pricing.sectionLabel') }}
      </h2>
      <p class="mx-auto mt-2 max-w-lg text-balance text-center font-serif text-4xl font-semibold text-gray-900 sm:text-5xl">
        {{ $t('pricing.sectionTitle') }}
      </p>
    </div>

    <p
      v-if="showHeader"
      class="mx-auto mt-6 max-w-2xl text-center text-lg/8 text-gray-600"
    >
      {{ $t('pricing.sectionSubtitle') }}
    </p>

    <div
      v-if="showFrequencyToggle"
      class="mt-10 flex justify-center"
    >
      <fieldset aria-label="Payment frequency">
        <RadioGroup
          v-model="frequency"
          class="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs/5 font-semibold ring-1 ring-inset ring-gray-200"
        >
          <RadioGroupOption
            v-for="option in frequencies"
            :key="option.value"
            v-slot="{ checked }"
            as="template"
            :value="option"
          >
            <div :class="[checked ? 'bg-primary-600 text-white' : 'text-gray-500', 'cursor-pointer rounded-full px-2.5 py-1']">
              {{ option.label }}
            </div>
          </RadioGroupOption>
        </RadioGroup>
      </fieldset>
    </div>

    <div class="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:max-w-6xl lg:grid-cols-3">
      <div
        v-for="tier in tiers"
        :key="tier.id"
        :class="[tier.mostPopular ? 'ring-2 ring-primary-600' : 'ring-1 ring-gray-200', 'relative rounded-3xl p-8']"
      >
        <h2
          :id="tier.id"
          :class="[tier.mostPopular ? 'text-primary-600' : 'text-gray-900', 'text-lg/8 font-semibold']"
        >
          {{ tier.name }}
        </h2>

        <p
          v-if="tier.mostPopular"
          class="absolute right-3 top-3 rounded-full bg-primary-600/10 px-2.5 py-1 text-xs/5 font-semibold text-primary-600"
        >
          {{ $t('pricing.mostPopular') }}
        </p>

        <p class="mt-4 text-sm/6 text-gray-600">
          {{ tier.description }}
        </p>
        <p class="mt-6 flex items-baseline gap-x-1">
          <ClientOnly>
            <NumberFlow
              :value="tier.price[frequency.value]"
              :format="{ style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }"
              class="text-4xl font-semibold tracking-tight text-gray-900"
            />
            <template #fallback>
              <span class="text-4xl font-semibold tracking-tight text-gray-900">{{ tier.priceFormatted[frequency.value] }}</span>
            </template>
          </ClientOnly>
          <span class="text-sm/6 font-semibold text-gray-600">{{ frequency.priceSuffix }}</span>
        </p>
        <p
          v-if="frequency.value === 'annually'"
          class="mt-1 text-xs text-gray-500"
        >
          {{ $t('pricing.billedAnnually', { amount: `${tier.price.annuallyTotal.toFixed(2)}€` }) }}
        </p>
        <component
          :is="ctaAction ? 'button' : NuxtLinkLocale"
          :to="!ctaAction ? '/app' : undefined"
          :aria-describedby="tier.id"
          :class="[tier.mostPopular ? 'bg-primary-600 text-white shadow-sm hover:bg-primary-500' : 'text-primary-600 ring-1 ring-inset ring-primary-200 hover:ring-primary-300', 'mt-6 block cursor-pointer rounded-md px-3 py-2 text-center text-sm/6 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600']"
          @click="handleCtaClick(tier)"
        >
          {{ ctaText || $t('pricing.createAccount') }}
        </component>
        <p class="mt-2 text-center text-xs text-gray-500">
          {{ $t('pricing.cancellableAnytime') }}
        </p>
        <ul
          role="list"
          class="mt-8 space-y-3 text-sm/6 text-gray-600"
        >
          <li
            v-for="feature in tier.features"
            :key="feature"
            class="flex gap-x-3"
          >
            <Icon
              name="heroicons:check"
              class="h-6 w-5 flex-none text-primary-600"
              aria-hidden="true"
            />
            {{ feature }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
